import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
	getIsAllPassengersSelected,
	getPassengersIds,
	getRefundLoading,
	getSelectedRefundFamilies,
	getSelectedReason,
	REFUND_ROUTES,
	setAllPassengerSelect
} from '../model';
import { useRouter } from '@shared/router';
import { useIsMobile } from '../../../../utils';
import { ExchangeRefundBreadcrumb } from '@entities/order';
import { getRefundPassengerLabel } from './refundPassengerUtils';
import { useSkipPassengers } from './useSkipPassengers';
import { useSkipReasons } from './useSkipReasons';
import { CdnIconNames } from '@shared/ui';

export const useRefundBreadcrumbs = (): ExchangeRefundBreadcrumb[] => {
	const { t } = useTranslation('Refund');
	const router = useRouter();
	const isMobile = useIsMobile();
	const dispatch = useDispatch();

	const isLoading = useSelector(getRefundLoading);
	const passengersIds = useSelector(getPassengersIds);
	const selectedReason = useSelector(getSelectedReason);
	const selectedFamilies = useSelector(getSelectedRefundFamilies);
	const isAllPassengersSelected = useSelector(getIsAllPassengersSelected);

	const skipPassengers = useSkipPassengers();
	const skipReasons = useSkipReasons();

	return useMemo<ExchangeRefundBreadcrumb[]>(() => {
		const breadcrumbs: ExchangeRefundBreadcrumb[] = [];

		if (router.currentPath === REFUND_ROUTES.BASE) {
			return breadcrumbs;
		}

		let onClickPassengers: (() => void) | undefined = () => {
			if (router.currentPath === REFUND_ROUTES.PASSENGERS) {
				dispatch(setAllPassengerSelect({ selected: false }));
			} else {
				router.navigate(REFUND_ROUTES.PASSENGERS);
			}
		};

		let onClickReasons: (() => void) | undefined = () => {
			if (router.currentPath !== REFUND_ROUTES.REASONS) {
				router.navigate(REFUND_ROUTES.REASONS);
			}
		};

		let passengersIcon: CdnIconNames | undefined = isMobile ? 'Info/Passengers' : 'Action/Close-Clear';
		let reasonsIcon: CdnIconNames | undefined = isMobile ? 'Services/Refundable' : undefined;

		if (skipPassengers) {
			onClickPassengers = undefined;
			passengersIcon = undefined;
		}

		if (skipReasons) {
			onClickReasons = undefined;
			reasonsIcon = undefined;
		}

		if (router.currentPath !== REFUND_ROUTES.PASSENGERS) {
			if (isAllPassengersSelected && passengersIds?.length > 1 && !isMobile) {
				breadcrumbs.push({
					id: 'passengers/all',
					label: t('Passengers.AllPassengers'),
					passengersCount: passengersIds.length,
					icon: passengersIcon,
					disabled: isLoading,
					onAction: onClickPassengers
				});
			} else {
				selectedFamilies.forEach(family =>
					breadcrumbs.push({
						id: `passenger/${family.id}`,
						label: getRefundPassengerLabel(family),
						icon: passengersIcon,
						disabled: isLoading,
						onAction: onClickPassengers
					})
				);
			}
		}

		if (router.currentPath !== REFUND_ROUTES.PASSENGERS && router.currentPath !== REFUND_ROUTES.REASONS) {
			if (selectedReason) {
				const category = t(selectedReason.category);
				const reason = t(selectedReason.reason);

				breadcrumbs.push({
					id: `reason/${selectedReason.reason}`,
					label: category,
					description: reason,
					icon: reasonsIcon,
					disabled: skipReasons ? false : isLoading,
					onAction: onClickReasons
				});
			}
		}

		return breadcrumbs;
	}, [
		t,
		router,
		isMobile,
		dispatch,
		isLoading,
		skipReasons,
		passengersIds,
		skipPassengers,
		selectedReason,
		selectedFamilies,
		isAllPassengersSelected
	]);
};
